import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Printer, IndianRupee, Calendar, CreditCard, Search, 
         AlertCircle, CheckCircle, X, FileText, Loader2, Receipt, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  getBillingInvoicesByPatient, 
  getBillingServices, 
  createBillingInvoice,
  updateBillingInvoice,
  recordInvoicePayment 
} from '@/lib/api';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import InvoiceEditModal from '@/components/Patient/InvoiceEditModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
// Import shared billing utilities
import { useBillingUtils } from '@/hooks/useBillingUtils';
// Remove unused import
// Import shared billing components
import { InvoiceCard } from '@/components/Billing/InvoiceCard';
import { PaymentRecordModal, InvoiceViewModal } from '@/components/Billing/BillingModals';
import { BillingSummaryCards, BillingListContainer } from '@/components/Billing/BillingUI';
// Import centralized types
import type { Invoice, BillingService } from '@/types';

interface BillingTabProps {
  patientId: string;
  patient: {
    id: string;
    name: string;
    phone?: string;
    address?: string;
  };
  doctorId: string;
  clinicId: string;
}

export default function BillingTab({ patientId, patient, doctorId, clinicId }: BillingTabProps) {
  // All useState hooks first
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [services, setServices] = useState<BillingService[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  
  // All useRef hooks (none currently used)

  // All custom hooks
  const { 
    formatCurrency, 
    formatDate, 
    getStatusConfig 
  } = useBillingUtils();

  // All useEffect hooks together
  useEffect(() => {
    loadInvoices();
    loadServices();
  }, [patientId, clinicId]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange, amountRange]);

  // Helper functions
  const showNotification = (type: 'success' | 'error', message: string) => {
    toast({
      title: type === 'success' ? 'Success' : 'Error',
      description: message,
      variant: type === 'success' ? 'default' : 'destructive',
    });
  };

  // Computed values
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = !searchTerm || 
      invoice.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      invoice.payment_status.toLowerCase() === statusFilter.toLowerCase();
    
    // Date range filter for patient billing history
    let matchesDate = true;
    if (dateRange.from || dateRange.to) {
      const invoiceDate = new Date(invoice.date);
      const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());
      
      if (dateRange.from && dateRange.to) {
        const fromDate = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
        const toDate = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
        matchesDate = invoiceDateOnly >= fromDate && invoiceDateOnly <= toDate;
      } else if (dateRange.from) {
        const fromDate = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
        matchesDate = invoiceDateOnly >= fromDate;
      } else if (dateRange.to) {
        const toDate = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
        matchesDate = invoiceDateOnly <= toDate;
      }
    }

    // Amount range filter
    const matchesAmountRange = (!amountRange.min || invoice.total_amount >= parseFloat(amountRange.min)) &&
      (!amountRange.max || invoice.total_amount <= parseFloat(amountRange.max));
    
    return matchesSearch && matchesStatus && matchesDate && matchesAmountRange;
  });

  // Filtered invoices computed

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getBillingInvoicesByPatient(patientId);
      setInvoices(data || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      showNotification('error', 'Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getBillingServices(clinicId, doctorId);
      setServices((data as any) || []);
    } catch (error) {
      console.error('Failed to load services:', error);
      showNotification('error', 'Failed to load billing services');
    }
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      setLoading(true);
      // Sanitize items: only allowed fields
      const sanitizedItems = invoiceData.items.map((item: any) => {
        const base = {
          service_name: item.service_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        };
        if (typeof item.notes === 'string' && item.notes) {
          return { ...base, notes: item.notes };
        }
        return base;
      });
      // patient_address must be a string
      const apiInvoiceData = {
        patient_id: patientId,
        patient_name: patient.name,
        patient_phone: patient.phone || '',
        patient_address: patient.address || '',
        doctor_id: doctorId,
        clinic_id: clinicId,
        items: sanitizedItems,
        discount_amount: invoiceData.discount || 0,
        payment_method: invoiceData.paymentMethod,
        paid_amount: invoiceData.paidAmount ?? invoiceData.finalTotal,
        notes: invoiceData.discount > 0 ? `Discount applied: ₹${invoiceData.discount}` : undefined
      };
      const response = await createBillingInvoice(apiInvoiceData);
      toast({
        title: "Success",
        description: "Invoice created successfully",
        variant: "default"
      });
      setShowCreateModal(false);
      // Refresh invoices list
      await loadInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: "Error",
        description: "Failed to create invoice",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    try {
      if (!selectedInvoice || paymentAmount <= 0) {
        showNotification('error', 'Please enter a valid payment amount');
        return;
      }

      if (paymentAmount > selectedInvoice.balance_amount) {
        showNotification('error', 'Payment amount cannot exceed balance due');
        return;
      }

      setLoading(true);
      await recordInvoicePayment(selectedInvoice.id, {
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_date: new Date().toISOString()
      });

      showNotification('success', 'Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentAmount(0);
      loadInvoices();
    } catch (error) {
      console.error('Failed to record payment:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to record payment';
      showNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleUpdateInvoice = async (invoiceData: any) => {
    try {
      if (!selectedInvoice) return;

      setLoading(true);
      await updateBillingInvoice(selectedInvoice.id, invoiceData);

      toast({
        title: "Success",
        description: "Invoice updated successfully",
        variant: "default"
      });

      setShowEditModal(false);
      setSelectedInvoice(null);
      loadInvoices(); // Refresh invoices list
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update invoice",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAction = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  // Enhanced stats calculation with better error handling
  const totalRevenue = invoices
    .filter(inv => inv.payment_status === 'Paid')
    .reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);
  
  const pendingAmount = invoices
    .filter(inv => inv.payment_status === 'Pending' || inv.payment_status === 'Partial')
    .reduce((sum, inv) => sum + (Number(inv.balance_amount) || Number(inv.total_amount) || 0), 0);
  
  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter(inv => inv.payment_status === 'Paid').length;
  
  // Calculate monthly revenue for this patient (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const monthlyRevenue = invoices
    .filter(inv => 
      inv.payment_status === 'Paid' && 
      new Date(inv.date) >= thirtyDaysAgo
    )
    .reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);

  // Quick stats for search filters
  const quickStats = {
    total: filteredInvoices.length,
    paid: filteredInvoices.filter(i => i.payment_status === 'Paid').length,
    pending: filteredInvoices.filter(i => i.payment_status === 'Pending').length,
    partial: filteredInvoices.filter(i => i.payment_status === 'Partial').length,
  };

  // Loading state
  if (loading && invoices.length === 0) {
    return (
      <div className="space-y-3">
        {/* Summary Cards Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        
        {/* Header Skeleton */}
        <Skeleton className="h-16 rounded-lg" />
        
        {/* Bills List Skeleton */}
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const handlePrintInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowPrintModal(true);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-2">
      {/* Notifications handled by toast system */}

      {/* Summary Cards */}
      <BillingSummaryCards
        totalRevenue={totalRevenue}
        pendingAmount={pendingAmount}
        totalInvoices={totalInvoices}
        paidInvoices={paidInvoices}
        monthlyRevenue={monthlyRevenue}
        context="patient"
      />

      {/* Header with Create Button */}
      <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 p-1.5 rounded bg-primary/10">
            <ClipboardList className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Patient Bills</h3>
            <p className="text-xs text-muted-foreground">Billing history for {patient.name}</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)} 
          size="sm" 
          className="px-4"
          title="Create new bill for patient"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Bill
        </Button>
      </div>

      {/* Bills List with Search */}
      <BillingListContainer
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        dateFilter="all"
        onDateChange={() => {}}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        amountRange={amountRange}
        onAmountRangeChange={setAmountRange}
        variant="patient"
        invoices={invoices}
        filteredInvoices={filteredInvoices}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onCreateInvoice={() => setShowCreateModal(true)}
      >
        <Card className="medical-card">
          <div className="divide-y divide-border">
        {filteredInvoices.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center mb-3">
                  <Receipt className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1">No bills found</h3>
                <p className="text-muted-foreground mb-3 text-xs">
                  {searchTerm || statusFilter !== 'all' || dateRange.from || dateRange.to || amountRange.min || amountRange.max
                    ? 'No bills match your current filters'
                    : `${patient.name} doesn't have any billing history yet`
                  }
                </p>
                <Button onClick={() => setShowCreateModal(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Bill
                </Button>
              </div>
            ) : (
              filteredInvoices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  variant="patient"
                  onView={handleViewInvoice}
                  onEdit={handleEditInvoice}
                  onPrint={handlePrintInvoice}
                  onPayment={handlePaymentAction}
                  className="invoice-card"
                />
              ))
            )}
                      </div>
      </Card>
      </BillingListContainer>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <MedicalBillingModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          patient={patient}
          services={services}
          onCreateInvoice={handleCreateInvoice}
          loading={loading}
          clinicId={clinicId}
          doctorId={doctorId}
          onRefreshServices={loadServices}
        />
      )}

      {/* View Invoice Modal */}
      <InvoiceViewModal
        isOpen={showViewModal}
        onClose={() => {
                  setShowViewModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onPrint={handlePrintInvoice}
        onPayment={handlePaymentAction}
        variant="patient"
      />

      {/* Payment Modal */}
      <PaymentRecordModal
        isOpen={showPaymentModal}
        onClose={() => {
                setShowPaymentModal(false);
                setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onRecordPayment={handleRecordPayment}
        loading={loading}
      />

      {/* Print Bill Preview Modal */}
      <PrintBillPreviewModal
        open={showPrintModal}
        invoice={selectedInvoice}
        onClose={() => {
          setShowPrintModal(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Invoice Edit Modal */}
      <InvoiceEditModal
        invoice={selectedInvoice ? {
          ...selectedInvoice,
          patient_id: selectedInvoice.patient_id || patientId,
          patient_address: selectedInvoice.patient_address || patient.address,
          doctor_id: selectedInvoice.doctor_id || doctorId,
          clinic_id: selectedInvoice.clinic_id || clinicId,
          subtotal: selectedInvoice.subtotal || selectedInvoice.total_amount,
          discount_amount: selectedInvoice.discount_amount || 0,
          tax_amount: selectedInvoice.tax_amount || 0,
          due_date: selectedInvoice.due_date || undefined,
          notes: selectedInvoice.notes || undefined,
          items: selectedInvoice.items || []
        } : null}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedInvoice(null);
        }}
        onSave={handleUpdateInvoice}
        services={services}
        loading={loading}
      />
    </div>
  );
}